<template>
  <div>
    <TFormGroup :field="r$.selectedType">
      <TRadioCardList
        :items="deliverRvOptions"
        :model-value="selectedType"
       
        @update:model-value="handleRvUsageChange"
      />
    </TFormGroup>

    <FormRvDeliveryAddress
      v-if="isDeliveryAvailable && isDelivering"
      :is-invalid="r$.hasDeliveryDestination.$error"
      :country-code="(rvCountry as COUNTRY_SHORTS)"
      :measurement-unit="rvMeasurementUnit"
      :delivery-max-distance="rvDeliveryMaxDistance"
      :delivery-minimum-cost-cents="rvDeliveryMinimumCostCents"
      :countries="allowedCountries"
      class="mt-4"
      hide-max-delivery-distance-message
      @delivery-address-updated="destinationUpdated(selectedRvUsage, $event)"
    />

    <TFormGroup
      v-else-if="isPickUpAvailable && selectedType === 'Pickup'"
      label-for="booking-destination"
      :label="t('pickup.destination.label')"
      :field="r$.hasDestination"
      disable-touch-on-blur
      class="mt-4"
    >
      <EstimatePlace
        input-id="booking-destination"
        :input-placeholder="t('pickup.destination.placeholder')"
        :destination="bookingEstimateParams.destination?.name"
        :countries="allowedCountries"
        @update-address="destinationUpdated('Pickup', $event)"
      />

      <EstimateCrossBorderTipbox
        v-if="crossBorderTravelDisallowed"
        :country="(rvCountry as COUNTRY_SHORTS)"
        class="mt-4"
      />
    </TFormGroup>

    <TFormGroup
      v-if="isDeliveryTypeRequired"
      label-for="stationary-after-delivery"
      :label="t('rvz28661SimplifiedRtbFlow.stationaryAfterDelivery.question')"
      :field="r$.deliveryType"
      class="mt-4"
    >
      <TRadioGroup
        id="stationary-after-delivery"
        :model-value="deliveryType"
        :options="stationaryOptionsRadio"
        stacked
        @update:model-value="handleDeliveryTypeChange($event as RvUsage)"
      />

      <div
        v-if="showStationaryProtectionNote"
        class="text-sm text-gray-500 mt-2"
      >
        {{ t('stationaryAfterDelivery.stationaryInsuranceNote', { rate: financialStationaryDiscountPercentage }) }}
      </div>
    </TFormGroup>
  </div>
</template>

<script lang="ts" setup>
import { required, requiredIf } from '@regle/rules'

import { VALIDATION_SCOPE_RTB_DESTINATION } from '~/constants/rv'
import type { BookingEstimateDestination, Nullable, RvUsage } from '~/types'
import type { COUNTRY_SHORTS } from '~/constants'
import type { RadioCardListItem } from '~/components/_tailwind/radio/types'

const { t } = useI18n()
const route = useRoute()

const {
  rvHasDelivery,
  rvFormattedLocation,
  rvIsDeliveryOnly,
  rvIsInsured,
  rvCountry,
  rvAllowCrossBorderTravel,
  rvMeasurementUnit,
  rvDeliveryMaxDistance,
  rvDeliveryMinimumCostCents,
} = useRvDetails()

const {
  bookingEstimateParams,
  hasPendingDeliveryType,
  selectedRvUsage,
  updateDestination,
  updateRvUsage,
  financialHasStationaryDiscount,
  financialStationaryDiscountPercentage,
} = useBookingEstimate()

const { deliveryCostError } = useDeliveryCost()

type SelectedType = Extract<RvUsage, 'Delivery' | 'Pickup'>

// Stationary delivery is answered by the follow-up question, so both delivery usages select the delivery card
const selectedType = computed<Nullable<SelectedType>>(() => {
  return selectedRvUsage.value === 'StationaryAfterDelivery' ? 'Delivery' : (selectedRvUsage.value ?? null)
})

const isDeliveryAvailable = computed(() => rvHasDelivery.value)

const isPickUpAvailable = computed(() => {
  return !rvIsDeliveryOnly.value
})

const isDelivering = computed(() => selectedType.value === 'Delivery')

const hasDestination = computed(() => Boolean(bookingEstimateParams.value.destination) || undefined)

// Prefill the answer from a previous visit once the delivery address is set, unless the guest left without answering
const deliveryType = ref<RvUsage>(isDelivering.value && hasDestination.value && !hasPendingDeliveryType.value ? selectedRvUsage.value : null)

const isDeliveryTypeRequired = computed(() => {
  return isDelivering.value && rvIsInsured.value && !rvIsDeliveryOnly.value && !deliveryCostError.value
})

watchEffect(() => {
  hasPendingDeliveryType.value = isDeliveryTypeRequired.value && !deliveryType.value
})

const hasValidDeliveryCost = computed(() => !deliveryCostError.value || undefined)

const showStationaryProtectionNote = computed(() => {
  return isDeliveryTypeRequired.value && deliveryType.value === 'StationaryAfterDelivery' && financialHasStationaryDiscount.value
})

const stationaryOptionsRadio = computed(() => {
  return [
    {
      'text': t('stationaryAfterDelivery.yes'),
      'value': 'Delivery',
      'data-testid': 'non-stationary-rv',
    },
    {
      'text': t('rvz28661SimplifiedRtbFlow.stationaryAfterDelivery.no'),
      'value': 'StationaryAfterDelivery',
      'data-testid': 'stationary-rv',
    },
  ]
})

const deliverRvOptions = computed(() => {
  const options: RadioCardListItem<SelectedType>[] = []

  if (isDeliveryAvailable.value) {
    options.push({
      label: t('rvz28661SimplifiedRtbFlow.options.delivery.title'),
      description: t('rvz28661SimplifiedRtbFlow.options.delivery.description', {
        distance: rvDeliveryMaxDistance.value,
        unit: rvMeasurementUnit.value,
      }),
      modelValue: 'Delivery',
      dataTestId: 'delivery-option',
    })
  }

  if (isPickUpAvailable.value) {
    options.push({
      label: t('rvz28661SimplifiedRtbFlow.options.pickup.title'),
      description: t('rvz28661SimplifiedRtbFlow.options.pickup.description', {
        location: rvFormattedLocation.value,
      }),
      modelValue: 'Pickup',
      dataTestId: 'pickup-option',
    })
  }

  return options
})

watch(selectedRvUsage, async (value) => {
  const storedRvUsage = bookingEstimateParams.value.rvUsage

  if (!value || !storedRvUsage || value === storedRvUsage) {
    return
  }

  updateRvUsage(value)

  await navigateTo({ query: { ...route.query, RvUsage: value } }, { replace: true })
}, { immediate: true })

async function updateRvUsageAndRoute(value: RvUsage) {
  updateRvUsage(value)

  await navigateTo({ query: { ...route.query, RvUsage: value || undefined } }, { replace: true })
}

async function handleRvUsageChange(value: Nullable<SelectedType>) {
  deliveryType.value = null

  await updateRvUsageAndRoute(value)

  r$.selectedType.$touch()
}

async function handleDeliveryTypeChange(value: RvUsage) {
  deliveryType.value = value
  r$.deliveryType.$touch()

  await updateRvUsageAndRoute(value)
}

const allowedCountries = computed(() => {
  if (rvAllowCrossBorderTravel.value || !rvCountry.value) {
    return []
  }
  return [rvCountry.value]
})

const crossBorderTravelDisallowed = computed(() => {
  if (rvAllowCrossBorderTravel.value) {
    return false
  }

  return bookingEstimateParams.value.destination?.country && bookingEstimateParams.value.destination?.country !== rvCountry.value
})

async function destinationUpdated(rvUsage: RvUsage, destinationData?: BookingEstimateDestination) {
  updateDestination(destinationData)

  updateRvUsage(rvUsage)

  r$.hasDestination.$touch()
  r$.hasDeliveryDestination.$touch()
  r$.hasValidDeliveryCost.$touch()
  r$.crossBorderTravelDisallowed.$touch()

  await navigateTo({
    query: {
      ...route.query,
      RvUsage: bookingEstimateParams.value.rvUsage ?? undefined,
      PlaceId: destinationData?.placeId ?? undefined,
    },
  }, { replace: true })
}

const { r$ } = useScopedRegle(
  {
    selectedType,
    hasDeliveryDestination: hasDestination,
    hasDestination,
    hasValidDeliveryCost,
    crossBorderTravelDisallowed,
    deliveryType,
  },
  {
    selectedType: {
      required,
    },
    hasDeliveryDestination: {
      required: requiredIf(() => isDeliveryAvailable.value && isDelivering.value),
    },
    hasDestination: {
      required: requiredIf(() => isPickUpAvailable.value && !isDelivering.value),
    },
    hasValidDeliveryCost: {
      required: requiredIf(() => isDeliveryAvailable.value && isDelivering.value),
    },
    crossBorderTravelDisallowed: {
      required: requiredIf(() => !rvAllowCrossBorderTravel.value),
    },
    deliveryType: {
      required: requiredIf(() => isDeliveryTypeRequired.value),
    },
  },
  {
    namespace: VALIDATION_SCOPE_RTB_DESTINATION,
  },
)
</script>

<i18n src="~~/i18n/locales/common/booking/destination.json" lang="json" />
